import {
  Column,
  Loading,
  Notice,
  Panel,
  Table,
} from "@appkit4/react-components";
import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import toast from "../../components/DismissibleToast";
import { NextButton } from "../../components/ProgressNav";
import {
  Button,
  Select,
  getIsReadonly,
} from "../../components/ReadonlyAwareInputs";
import { AuthContext } from "../../services/AuthProvider";
import { getGroupID, getGroupName } from "../../services/GroupContext";
import sqlService from "../../services/sqldatabase/sqldatabase.service";
import { AccessRoles } from "../../utils/AccessRoles";
import NewUserModal from "./NewUserModal";

const UserAccess = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const auth = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState();
  const userGroupRole = useSelector((state) => state.usageInfo.userGroupRole);
  const isAdminOrSupervisor =
    useSelector((state) => state.usageInfo.isTenantAdmin) ||
    userGroupRole === "Admin" ||
    userGroupRole === "Supervisor";
  const tenantId = useSelector((state) => state.usageInfo.tenantID);
  const user = useSelector((state) => state.usageInfo.user);

  useEffect(() => {
    setIsLoading(true);
    const getUsers = async () => {
      const response = await sqlService?.getGroupUsers(tenantId, getGroupID());
      if (response?.status === 200) {
        setUsers(response?.data);
      } else if (response?.status === 404) {
        toast.error(`Group ${getGroupName()} not found`);
      } else {
        toast.error("Could not fetch users");
      }
      setIsLoading(false);
    };
    getUsers();
    setCurrentUserEmail(user?.email);
  }, [user]);

  const addUser = (email, role) => {
    if (!email) {
      throw new Error("no email provided");
    }

    const isEmail = (email) => {
      const emailFormat = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;
      return !!email?.match(emailFormat);
    };
    if (!isEmail(email)) {
      throw new Error("email provided is invalid");
    }

    if (!role) {
      throw new Error("no role provided");
    }

    if (!AccessRoles()?.includes(role)) {
      throw new Error("not a valid role");
    }

    const newUsers = [...users];
    newUsers?.push({
      email: email,
      role: role,
    });
    setUsers(newUsers);
  };
  const deleteUser = (email) => {
    const index = users?.findIndex((user) => user?.email === email);
    if (index > -1) {
      const tempUsers = [...users];
      tempUsers?.splice(index, 1);
      setUsers(tempUsers);
    }
  };

  const changeUserRole = (email, newRole) => {
    const newUsers = users.map((user) =>
      user.email === email ? { email, role: newRole } : user,
    );
    setUsers(newUsers);
  };

  const canModify = (user) => {
    return isAdminOrSupervisor && user.email !== currentUserEmail;
  };

  const submit = () => {
    if (!auth?.userCanManageAccess()) {
      return;
    }
    setIsPosting(true);
    let newGroupUsers = users?.map((user) => {
      return { email: user?.email, role: user?.role };
    });
    const result = sqlService?.postGroupUsers(
      tenantId,
      getGroupID(),
      newGroupUsers,
    );

    const onSuccessOrError = () => setIsPosting(false);
    return toast.save(result, onSuccessOrError);
  };

  if (isLoading) {
    return (
      <Loading
        loadingType="linear"
        indeterminate={true}
        compact={false}
        className="page-loader"
      />
    );
  }
  return (
    <div className="ap-container">
      {!(getIsReadonly() || auth?.userCanManageAccess()) && (
        <div className="container-fluid">
          <Notice
            message="You don't have permissions to modify user roles, changes will not be saved."
            status="icon-warning-fill"
            closeable={false}
            style={{ backgroundColor: "white", width: "100%" }}
          />
        </div>
      )}
      <Panel title={`Manage Access`} className="mt-4 mb-4">
        <p>
          This page allows the user to grant, revoke or change access to the CIR
          Analyser for the group. You must select one of the following roles,
          depending on the level of access you wish to grant to the user:
        </p>
        <ul>
          <li>Reader - view only access</li>
          <li>Editor - view and edit access</li>
          <li>Supervisor - view, edit and access management</li>
        </ul>
      </Panel>
      <Panel title={`Manage Access`}>
        <div className="d-flex justify-content-end ap-container mt-4 mb-4">
          <Button
            kind="primary"
            icon="icon-plus-outline"
            disabled={!isAdminOrSupervisor}
            onClick={() => setShowAddUserModal(true)}
          >
            Add user
          </Button>
          <NewUserModal
            visible={showAddUserModal}
            setVisible={setShowAddUserModal}
            addUser={addUser}
            existingUsers={users}
          ></NewUserModal>
        </div>

        <Table originalData={users} hasTitle>
          <Column field="email" style={{ width: "30%" }}>
            Email
          </Column>
          <Column
            field="role"
            renderCell={(row) => (
              <>
                <Select
                  data={AccessRoles()}
                  style={{ width: "40%" }}
                  dropdownRenderMode="portal"
                  disabled={!canModify(row)}
                  placeholder="Role"
                  value={row["role"]}
                  onSelect={(newRole) => changeUserRole(row.email, newRole)}
                ></Select>
              </>
            )}
          >
            Access Level
          </Column>
          <Column
            field="email"
            style={{ width: 50 }}
            renderCell={(row) => (
              <>
                <Button
                  kind="secondary"
                  style={{ width: 35 }}
                  compact
                  disabled={!canModify(row)}
                  onClick={() => deleteUser(row?.email)}
                >
                  <span className="Appkit4-icon icon-close-outline"></span>
                </Button>
              </>
            )}
          ></Column>
        </Table>

        <div className="d-flex justify-content-end ap-container mt-4 mb-4 d-flex gap-2">
          <Button
            hiddenInReadonly
            onClick={submit}
            loading={isPosting}
            kind="secondary"
          >
            Save
          </Button>
          <NextButton loading={isPosting} preNavigation={submit} kind="primary">
            Next page
          </NextButton>
        </div>
      </Panel>
    </div>
  );
};

export default UserAccess;
